import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workSchedulesApi, type WorkSchedule } from '@/api/hr';
import { useConfirm } from '@/components/ConfirmProvider';
import Drawer from '@/components/Drawer';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * Довідник графіків роботи (tenant-scoped, alembic 0296).
 *
 * Графік задає набір робочих днів тижня (пн-нд) + годин/тиждень. Тип — лише
 * «П'ятиденка» (FIVE_DAY): норма рахується за виробничим календарем (робочий
 * день графіка ∩ робочий день календаря). Системний графік 🔒 не видаляється.
 * Використовується при прийомі на роботу + авто-заповненні табеля за графіком.
 * Permission: payroll.manage (enforcer + RLS tenant-scoped).
 */
const DAYS: { key: keyof WorkSchedule; label: string }[] = [
  { key: 'works_mon', label: 'Понеділок' },
  { key: 'works_tue', label: 'Вівторок' },
  { key: 'works_wed', label: 'Середа' },
  { key: 'works_thu', label: 'Четвер' },
  { key: 'works_fri', label: "П'ятниця" },
  { key: 'works_sat', label: 'Субота' },
  { key: 'works_sun', label: 'Неділя' },
];

interface FormState {
  id: string | null;
  code: string;
  name: string;
  hours_per_week: number;
  works_mon: boolean;
  works_tue: boolean;
  works_wed: boolean;
  works_thu: boolean;
  works_fri: boolean;
  works_sat: boolean;
  works_sun: boolean;
  is_active: boolean;
  sort_order: number;
}

const EMPTY: FormState = {
  id: null, code: '', name: '', hours_per_week: 40,
  works_mon: true, works_tue: true, works_wed: true, works_thu: true,
  works_fri: true, works_sat: false, works_sun: false,
  is_active: true, sort_order: 100,
};

function daysSummary(s: WorkSchedule): string {
  const on = DAYS.filter((d) => s[d.key]).map((d) => d.label.slice(0, 2));
  return on.length ? on.join(', ') : '—';
}

export default function WorkSchedulesPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);

  const list = useQuery({
    queryKey: ['work-schedules-all'],
    queryFn: () => workSchedulesApi.list(false),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['work-schedules-all'] });
    qc.invalidateQueries({ queryKey: ['work-schedules'] }); // dropdown прийому
  };

  const save = useMutation({
    mutationFn: () => {
      const f = form!;
      const body = {
        code: f.code.trim(), name: f.name.trim(),
        schedule_type: 'FIVE_DAY' as const,
        hours_per_week: f.hours_per_week,
        works_mon: f.works_mon, works_tue: f.works_tue, works_wed: f.works_wed,
        works_thu: f.works_thu, works_fri: f.works_fri, works_sat: f.works_sat,
        works_sun: f.works_sun, is_active: f.is_active, sort_order: f.sort_order,
      };
      if (f.id) {
        const { code: _c, ...rest } = body;
        return workSchedulesApi.update(f.id, rest);
      }
      return workSchedulesApi.create(body);
    },
    onSuccess: () => { invalidate(); setForm(null); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => workSchedulesApi.remove(id),
    onSuccess: () => { invalidate(); setForm(null); },
  });

  const startEdit = (s: WorkSchedule) => setForm({
    id: s.id, code: s.code, name: s.name,
    hours_per_week: Number(s.hours_per_week),
    works_mon: s.works_mon, works_tue: s.works_tue, works_wed: s.works_wed,
    works_thu: s.works_thu, works_fri: s.works_fri, works_sat: s.works_sat,
    works_sun: s.works_sun, is_active: s.is_active, sort_order: s.sort_order,
  });

  const rows = list.data ?? [];
  const canSave = !!form && !!form.code.trim() && !!form.name.trim() && !save.isPending;

  const cols = useMemo<ColumnDef<WorkSchedule>[]>(() => [
    {
      key: 'code', header: 'Код', width: 130,
      sortValue: (s) => s.code ?? '',
      cell: (s) => <>{s.is_system && '🔒 '}{s.external_source && 'І '}{s.code}</>,
    },
    {
      key: 'name', header: 'Назва',
      sortValue: (s) => (s.name ?? '').toLowerCase(), cell: (s) => s.name,
    },
    {
      key: 'days', header: 'Робочі дні', width: 240,
      cell: (s) => daysSummary(s),
    },
    {
      key: 'hours_per_week', header: 'Год./тиждень', width: 120, align: 'center',
      sortValue: (s) => Number(s.hours_per_week),
      cell: (s) => Number(s.hours_per_week).toFixed(2),
    },
    {
      key: 'actions', header: '', width: 70, align: 'center',
      cell: (s) => !s.is_system && (
        <button className="danger" title="Деактивувати"
          onClick={() => confirm(`Деактивувати графік «${s.name}»?`).then((ok) => {
            if (ok) remove.mutate(s.id);
          })}>✕</button>
      ),
    },
  ], [confirm, remove]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Графіки роботи</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
                onClick={() => setForm({ ...EMPTY })}>
          + Новий
        </button>
      </div>
      <p style={{ color: 'var(--muted)', maxWidth: 760 }}>
        Графік задає робочі дні тижня та норму годин. Тип — п'ятиденка: норма
        днів/годин рахується за виробничим календарем (робочий день графіка, що
        не є святом/вихідним). Системний графік 🔒 не видаляється. Графік
        обирається на картці співробітника і використовується для авто-заповнення
        табеля.
      </p>

      <DataTable<WorkSchedule>
        rows={rows}
        columns={cols}
        rowKey={(s) => s.id}
        loading={list.isLoading}
        emptyText="Немає графіків."
        onRowDoubleClick={startEdit}
        rowMuted={(s) => s.is_active ? undefined : 0.5}
        initialSortKey="code"
      />

      {form && (
        <Drawer
          open={!!form}
          title={form.id ? 'Редагувати графік' : 'Новий графік'}
          onClose={() => setForm(null)}
          footer={
            <>
              <button onClick={() => setForm(null)}>Скасувати</button>
              <button className="primary" disabled={!canSave} onClick={() => save.mutate()}>
                Зберегти
              </button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label>Код
              <input value={form.code} disabled={!!form.id}
                onChange={(e) => setForm({ ...form, code: e.target.value })} />
            </label>
            <label>Найменування
              <input value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label>Тип графіка
              <select value="FIVE_DAY" disabled>
                <option value="FIVE_DAY">П'ятиденка</option>
              </select>
            </label>
            <label>Годин на тиждень
              <input type="number" min={0} max={168} step={0.5}
                value={form.hours_per_week}
                onChange={(e) => setForm({ ...form, hours_per_week: Number(e.target.value) })} />
            </label>
            <fieldset style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '0.5rem 0.75rem' }}>
              <legend style={{ padding: '0 0.4rem', color: 'var(--muted)' }}>Робочий час</legend>
              {DAYS.map((d) => (
                <label key={d.key as string}
                  style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <input type="checkbox" checked={form[d.key as keyof FormState] as boolean}
                    onChange={(e) => setForm({ ...form, [d.key]: e.target.checked })} />
                  {d.label}
                </label>
              ))}
            </fieldset>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Активний
            </label>
          </div>
        </Drawer>
      )}
    </div>
  );
}
